import {
  useFloating,
  offset as offsetFunc,
  flip,
  shift,
  autoUpdate,
} from '@floating-ui/vue'

import type { Placement } from '@floating-ui/vue'
import type { CSSProperties } from 'vue'
import type { AppHoverCardProps } from '../types/interfaces'

interface AppHoverCardOptions {
  props: AppHoverCardProps
}

interface AppHoverCardReturn {
  cardId: string
  triggerRef: Ref<HTMLElement | null>
  cardRef: Ref<HTMLElement | null>
  show: ComputedRef<boolean>
  cardState: ComputedRef<'open' | 'closed'>
  floatingStyles: Readonly<Ref<CSSProperties>>
  resolvedPlacement: Readonly<Ref<Placement>>
  resolvedSide: ComputedRef<string>
  teleportTarget: ComputedRef<string>
  showCard: () => void
  hideCard: () => void
  cancelHide: () => void
  toggleCard: () => void
  setAnimationEnd: () => void
  setKeydown: (event: KeyboardEvent) => void
  setCardEnter: () => void
  setCardLeave: () => void
  setTriggerFocusout: (event: FocusEvent) => void
  setCardFocusout: (event: FocusEvent) => void
}

export const useAppHoverCard = ({ props }: AppHoverCardOptions): AppHoverCardReturn => {
  const cardId = useId()

  const isVisible = ref(false)
  const isRendered = ref(false)
  const isCardHovered = ref(false)
  const triggerRef = ref<HTMLElement | null>(null)
  const cardRef = ref<HTMLElement | null>(null)

  const cardState = computed(() => (isVisible.value ? 'open' : 'closed'))
  const show = computed(() => isRendered.value)

  let showTimeout: ReturnType<typeof setTimeout> | null = null
  let hideTimeout: ReturnType<typeof setTimeout> | null = null

  const { floatingStyles, placement: resolvedPlacement } = useFloating(
    triggerRef,
    cardRef,
    {
      placement: computed(() => props.placement ?? 'bottom'),
      strategy: 'fixed',
      whileElementsMounted: autoUpdate,
      middleware: computed(() => [
        offsetFunc(props.offset ?? 8),
        flip(),
        shift({ padding: 8 }),
      ]),
    },
  )

  const resolvedSide = computed(() => resolvedPlacement.value.split('-')[0] ?? resolvedPlacement.value)

  const teleportTarget = computed(() => {
    if (props.teleportTo === false) return 'body'
    if (!props.teleportTo) return 'body'

    if (props.teleportTo !== 'body' && !document.querySelector(props.teleportTo)) return 'body'

    return props.teleportTo
  })

  const clearTimeouts = (): void => {
    if (showTimeout) {
      clearTimeout(showTimeout)
      showTimeout = null
    }
    if (hideTimeout) {
      clearTimeout(hideTimeout)
      hideTimeout = null
    }
  }

  const hideImmediate = (): void => {
    clearTimeouts()
    isVisible.value = false
    isRendered.value = false
    isCardHovered.value = false
  }

  const showCard = (): void => {
    clearTimeouts()

    showTimeout = setTimeout(() => {
      isVisible.value = true
      isRendered.value = true
    }, props.delay ?? 100)
  }

  const hideCard = (): void => {
    clearTimeouts()

    hideTimeout = setTimeout(() => {
      isVisible.value = false
    }, 100)
  }

  const cancelHide = (): void => {
    if (hideTimeout) {
      clearTimeout(hideTimeout)
      hideTimeout = null
    }
  }

  const toggleCard = (): void => {
    if (isVisible.value) {
      hideCard()
    } else {
      showCard()
    }
  }

  const setAnimationEnd = (): void => {
    if (!isVisible.value) {
      isRendered.value = false
      isCardHovered.value = false
    }
  }

  const setKeydown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape') return

    clearTimeouts()
    isVisible.value = false
  }

  const setCardEnter = (): void => {
    isCardHovered.value = true
    cancelHide()

    if (isRendered.value && !isVisible.value) {
      isVisible.value = true
    }
  }

  const setCardLeave = (): void => {
    isCardHovered.value = false
    hideCard()
  }

  const setTriggerFocusout = (event: FocusEvent): void => {
    const relatedTarget = event.relatedTarget as Node | null

    if (isCardHovered.value || cardRef.value?.contains(relatedTarget)) return

    hideCard()
  }

  const setCardFocusout = (event: FocusEvent): void => {
    const relatedTarget = event.relatedTarget as Node | null

    if (isCardHovered.value) return
    if (cardRef.value?.contains(relatedTarget) || triggerRef.value?.contains(relatedTarget)) return

    hideCard()
  }

  const setScroll = (event: Event): void => {
    const target = event.target as HTMLElement | null

    if (target?.contains(triggerRef.value)) {
      hideImmediate()
    }
  }

  const setTouchOutside = (event: TouchEvent): void => {
    const target = event.target as Node | null

    if (!triggerRef.value?.contains(target) && !cardRef.value?.contains(target)) {
      hideCard()
    }
  }

  const startListeners = (): void => {
    window.addEventListener('scroll', setScroll, true)
    document.addEventListener('touchstart', setTouchOutside)
  }

  const stopListeners = (): void => {
    window.removeEventListener('scroll', setScroll, true)
    document.removeEventListener('touchstart', setTouchOutside)
  }

  watch(isVisible, (visible) => {
    if (visible) {
      startListeners()
    } else {
      stopListeners()
    }
  })

  onBeforeUnmount(() => {
    clearTimeouts()
    stopListeners()
  })

  return {
    cardId,
    triggerRef,
    cardRef,
    show,
    cardState,
    floatingStyles,
    resolvedPlacement,
    resolvedSide,
    teleportTarget,
    showCard,
    hideCard,
    cancelHide,
    toggleCard,
    setAnimationEnd,
    setKeydown,
    setCardEnter,
    setCardLeave,
    setTriggerFocusout,
    setCardFocusout,
  }
}
